import { lazy, Suspense } from "react";
import { DomainGuard } from "@/components/DomainGuard";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { ToastErrorBoundary } from "@/components/ToastErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LEGACY_REDIRECTS } from "@/lib/seoShared";
import { AuthProvider } from "./hooks/useAuth";
import { CartProvider } from "./hooks/useCart";
import { RequireRole } from "@/components/RequireRole";
import ScrollToTop from "./components/ScrollToTop";
import { AutoSEO } from "./components/AutoSEO";
import YandexMetrika from "./components/YandexMetrika";
import { TelegramIdCapture } from "./components/TelegramIdCapture";

const Index = lazy(() => import("./pages/Index"));
const Catalog = lazy(() => import("./pages/Catalog"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const Auth = lazy(() => import("./pages/Auth"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const Account = lazy(() => import("./pages/Account"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const Partner = lazy(() => import("./pages/Partner"));
const Partnership = lazy(() => import("./pages/Partnership"));
const Loyalty = lazy(() => import("./pages/Loyalty"));
const Checkout = lazy(() => import("./pages/Checkout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const ProductsAdmin = lazy(() => import("./pages/ProductsAdmin"));
const SupplierOrders = lazy(() => import("./pages/SupplierOrders"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
const OperatorDashboard = lazy(() => import("./pages/OperatorDashboard"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Guide = lazy(() => import("./pages/Guide"));
const GuideEditor = lazy(() => import("./pages/GuideEditor"));
const PeptideGuideIndex = lazy(() => import("./pages/PeptideGuideIndex"));
const PeptidePage = lazy(() => import("./pages/PeptidePage"));
const GoalsIndex = lazy(() => import("./pages/GoalsIndex"));
const GoalPage = lazy(() => import("./pages/GoalPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Calculator = lazy(() => import("./pages/Calculator"));
const LongevityPrograms = lazy(() => import("./pages/LongevityPrograms"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Delivery = lazy(() => import("./pages/Delivery"));
const Terms = lazy(() => import("./pages/Terms"));
const Oferta = lazy(() => import("./pages/Oferta"));
const Consent = lazy(() => import("./pages/Consent"));
const About = lazy(() => import("./pages/About"));
const Contacts = lazy(() => import("./pages/Contacts"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Journal = lazy(() => import("./pages/Journal"));
const JournalPost = lazy(() => import("./pages/JournalPost"));
const ReferralPartners = lazy(() => import("./pages/ReferralPartners"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const RegistrationSuccess = lazy(() => import("./pages/RegistrationSuccess"));
const Book = lazy(() => import("./pages/Book"));
const VideoSeries = lazy(() => import("./pages/VideoSeries"));
const BookPeptide = lazy(() => import("./pages/BookPeptide"));
const CartRedirect = lazy(() => import("./pages/CartRedirect"));
const ResearchUseOnly = lazy(() => import("./pages/ResearchUseOnly"));
const Quality = lazy(() => import("./pages/Quality"));
const QualityGuide = lazy(() => import("./pages/QualityGuide"));
const AiUsage = lazy(() => import("./pages/AiUsage"));
const Coa = lazy(() => import("./pages/Coa"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFail = lazy(() => import("./pages/PaymentFail"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Self-hosted инстанс без Realtime: данные не должны «залипать» в кэше
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
    },
  },
});


const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <ToastErrorBoundary>
            <Toaster />
            <Sonner />
          </ToastErrorBoundary>
          <BrowserRouter>
            <ScrollToTop />
            <DomainGuard />
            <AutoSEO />
            <YandexMetrika />
            <TelegramIdCapture />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                <Route path="/" element={<Index />} />
                <Route path="/catalog" element={<Catalog />} />
                <Route path="/catalog/:category" element={<CategoryPage />} />
                <Route path="/products/:slug" element={<ProductPage />} />
                <Route path="/product/:slug" element={<ProductPage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/staff-login" element={<StaffLogin />} />
                <Route path="/account" element={<Account />} />
                <Route path="/account/settings" element={<ProfileSettings />} />
                <Route path="/partner" element={<Partner />} />
                <Route path="/partnership" element={<Partnership />} />
                <Route path="/loyalty" element={<Loyalty />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/admin" element={<RequireRole roles={["admin"]}><AdminDashboard /></RequireRole>} />
                <Route path="/admin/analytics" element={<RequireRole roles={["admin"]}><AdminAnalytics /></RequireRole>} />
                <Route path="/admin/products" element={<RequireRole roles={["admin"]}><ProductsAdmin /></RequireRole>} />
                <Route path="/admin/supplier-orders" element={<RequireRole roles={["admin"]}><SupplierOrders /></RequireRole>} />
                <Route path="/manager/dashboard" element={<RequireRole roles={["admin","manager"]}><ManagerDashboard /></RequireRole>} />
                <Route path="/operator" element={<RequireRole roles={["admin","operator"]}><OperatorDashboard /></RequireRole>} />
                <Route path="/operator/dashboard" element={<RequireRole roles={["admin","operator"]}><OperatorDashboard /></RequireRole>} />
                <Route path="/order-success" element={<OrderSuccess />} />
                <Route path="/guide" element={<PeptideGuideIndex />} />
                <Route path="/guide/:slug" element={<PeptidePage />} />
                <Route path="/peptides/:slug" element={<PeptidePage />} />
                <Route path="/goals" element={<GoalsIndex />} />
                <Route path="/goals/:slug" element={<GoalPage />} />
                <Route path="/guide-book" element={<Guide />} />
                <Route path="/admin/guide-editor" element={<RequireRole roles={["admin"]}><GuideEditor /></RequireRole>} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/longevity" element={<LongevityPrograms />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/delivery" element={<Delivery />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/oferta" element={<Oferta />} />
                <Route path="/consent" element={<Consent />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/about" element={<About />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/journal/:id" element={<JournalPost />} />
                <Route path="/partners/referral" element={<ReferralPartners />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/registration-success" element={<RegistrationSuccess />} />
                <Route path="/track" element={<TrackOrder />} />
                <Route path="/book" element={<Book />} />
                <Route path="/video" element={<VideoSeries />} />
                <Route path="/en/video" element={<VideoSeries />} />
                <Route path="/book/:slug" element={<BookPeptide />} />
                {["en", "es", "fr", "de", "th"].flatMap((l) => [
                  <Route key={`${l}-book`} path={`/${l}/book`} element={<Book />} />,
                  <Route key={`${l}-book-slug`} path={`/${l}/book/:slug`} element={<BookPeptide />} />,
                ])}
                <Route path="/cart" element={<CartRedirect />} />
                <Route path="/research-use-only" element={<ResearchUseOnly />} />
                <Route path="/quality" element={<Quality />} />
                <Route path="/quality/:slug" element={<QualityGuide />} />
                <Route path="/ai-usage" element={<AiUsage />} />
                <Route path="/coa" element={<Coa />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/fail" element={<PaymentFail />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
